import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from '../images/auctiondetailsimages.png';
// import '../component/Auctionsdetails.css';

gsap.registerPlugin(ScrollTrigger);

const FEATURES = [
    'Anti-sniping timer extensions',
    'Hard close & live-style auctions',
    'Real-time bid history',
    'Featured & scheduled drops',
    'Outbid instant notifications',
];

const HEADING = 'Every second counts, every bid changes the game';

function Auctionsdetails() {
    const sectionRef = useRef(null);
    const badgeRef = useRef(null);
    const headingRef = useRef(null);
    const paragraphRef = useRef(null);
    const listRef = useRef(null);
    const buttonsRef = useRef(null);
    const imageWrapRef = useRef(null);
    const imageRef = useRef(null);
    const primaryBtnRef = useRef(null); 
    const secondaryBtnRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const words = headingRef.current.querySelectorAll('.word');

            // --- Entrance timeline (fires once, on enter) ---
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top 75%',
                    once: true,
                },
            });

            tl.from(badgeRef.current, {
                opacity: 0,
                y: 20,
                scale: 0.9,
                duration: 0.5,
                ease: 'power2.out',
            })
                .from(
                    words,
                    {
                        opacity: 0,
                        y: 40,
                        rotateX: -60,
                        transformOrigin: '50% 100%',
                        duration: 0.7,
                        stagger: 0.045,
                        ease: 'power3.out',
                    },
                    '-=0.2'
                )
                .from(
                    paragraphRef.current,
                    { opacity: 0, y: 20, duration: 0.5, ease: 'power2.out' },
                    '-=0.35'
                )
                .from(
                    listRef.current.children,
                    {
                        opacity: 0,
                        x: -25,
                        duration: 0.45,
                        stagger: 0.09,
                        ease: 'back.out(1.7)',
                    },
                    '-=0.25'
                )
                .from(
                    buttonsRef.current.children,
                    {
                        opacity: 0,
                        y: 15,
                        scale: 0.92,
                        duration: 0.4,
                        stagger: 0.1,
                        ease: 'back.out(2)',
                    },
                    '-=0.2'
                )
                .fromTo(
                    imageWrapRef.current,
                    { clipPath: 'inset(0% 0% 0% 100%)' },
                    { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.9, ease: 'power3.inOut' },
                    '-=0.6'
                )
                .from(
                    imageRef.current,
                    { scale: 1.25, duration: 0.9, ease: 'power3.out' },
                    '<'
                );

            // --- Badge idle glow pulse ---
            gsap.to(badgeRef.current, {
                boxShadow: '0 0 18px rgba(143, 239, 107, 0.35)',
                repeat: -1,
                yoyo: true,
                duration: 1.8,
                ease: 'sine.inOut',
                delay: 1,
            });

            // --- Image parallax while scrolling through section ---
            gsap.to(imageRef.current, {
                yPercent: -10,
                ease: 'none',
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.6,
                },
            });

            // --- Heading subtle depth shift on scroll ---
            gsap.to(headingRef.current, {
                y: -20,
                ease: 'none',
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.6,
                },
            });

            // --- Magnetic buttons ---
            const attachMagnetic = (el, strength = 18) => {
                if (!el) return () => {};
                const xTo = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power3' });
                const yTo = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power3' });

                const onMove = (e) => {
                    const rect = el.getBoundingClientRect();
                    const relX = e.clientX - rect.left - rect.width / 2;
                    const relY = e.clientY - rect.top - rect.height / 2;
                    xTo((relX / rect.width) * strength);
                    yTo((relY / rect.height) * strength);
                };
                const onLeave = () => {
                    xTo(0);
                    yTo(0);
                };

                el.addEventListener('mousemove', onMove);
                el.addEventListener('mouseleave', onLeave);
                return () => {
                    el.removeEventListener('mousemove', onMove);
                    el.removeEventListener('mouseleave', onLeave);
                };
            };

            const cleanupPrimary = attachMagnetic(primaryBtnRef.current, 12);
            const cleanupSecondary = attachMagnetic(secondaryBtnRef.current, 12);

            return () => {
                cleanupPrimary();
                cleanupSecondary();
            };
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        // FIX: background now lives on the full-width <section>, while the
        // grid content is wrapped in an inner .auctiondetails-container that
        // carries the max-width: 1760px constraint. Previously maxWidth was
        // on the section itself, so the background was clipped to 1760px too.
        <section className="auctiondetails-section" ref={sectionRef}>
            <div className="auctiondetails-container">
                {/* Left column — copy */}
                <div>
                    <span
                        ref={badgeRef}
                        style={{
                            display: 'inline-block',
                            padding: '8px 18px',
                            borderRadius: '999px',
                            border: '1px solid rgba(255,255,255,0.25)',
                            color: '#e8e8e8',
                            fontFamily: "'Outfit', sans-serif",
                            fontSize: '13px',
                            fontWeight: 500,
                            marginBottom: '28px',
                            backdropFilter: 'contrast(0.5)',
                        }}
                    >
                        Live Auctions — Where Timing Is Everything
                    </span>

                    <h2
                        ref={headingRef}
                        style={{
                            fontFamily: "'varino', 'CustomFont', sans-serif",
                            fontSize: 'clamp(32px, 4vw, 48px)',
                            lineHeight: 1.15,
                            textTransform: 'uppercase',
                            color: '#ffff',
                            WebkitTextStroke: ' 1px #83A551',
                            textShadow: '0 0 18px rgba(143, 239, 107, 0.35)',
                            margin: '0 0 24px',
                            letterSpacing: '0.01em',
                            perspective: '600px',
                        }}
                    >
                        {HEADING.split(' ').map((word, i) => (
                            <span
                                key={`${word}-${i}`}
                                className="word"
                                style={{ display: 'inline-block', marginRight: '0.28em' }}
                            >
                                {word}
                            </span>
                        ))}
                    </h2>

                    <p
                        ref={paragraphRef}
                        style={{
                            fontFamily: "'Outfit', sans-serif",
                            fontSize: '15px',
                            lineHeight: 1.7,
                            color: '#fff',
                            maxWidth: '480px',
                            margin: '0 0 24px',
                        }}
                    >
                        This is where tension lives. Our timed auction system brings real
                        stakes to every card listing — with anti-sniping extensions, live
                        bid updates, and real-time competitive pressure. No passive
                        scrolling. No boring listings. Just raw bidding energy until the
                        final second hits zero.
                    </p>

                    <ul
                        ref={listRef}
                        style={{
                            listStyle: 'none',
                            margin: '0 0 36px',
                            padding: 0,
                            fontFamily: "'Outfit', sans-serif",
                            fontSize: '14px',
                            color: '#fff',
                        }}
                    >
                        {FEATURES.map((feature) => (
                            <li
                                key={feature}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '6px 0',
                                }}
                            >
                                <span style={{ color: '#8fef6b' }}>•</span>
                                {feature}
                            </li>
                        ))}
                    </ul>

                    <div ref={buttonsRef} style={{ display: 'flex', gap: '14px' }}>
                        <button
                            ref={primaryBtnRef}
                            type="button"
                            style={{
                                fontFamily: "'Outfit', sans-serif",
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#0a0a0a',
                                background: '#83A551',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '14px 26px',
                                cursor: 'pointer',
                            }}
                        >
                            Join Live Auctions
                        </button>

                        <button
                            ref={secondaryBtnRef}
                            type="button"
                            style={{
                                fontFamily: "'Outfit', sans-serif",
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#fff',
                                background: 'transparent',
                                border: '1px solid rgba(255,255,255,0.25)',
                                borderRadius: '10px',
                                padding: '14px 26px',
                                cursor: 'pointer',
                            }}
                        >
                            Set Watchlist Alerts
                        </button>
                    </div>
                </div>

                {/* Right column — image */}
                <div
                    ref={imageWrapRef}
                    style={{
                        borderRadius: '20px',
                        overflow: 'hidden',
                        height: '100%',
                        minHeight: '520px',
                    }}
                >
                    <img
                        ref={imageRef}
                        src={Image}
                        alt="Live card auction in progress"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                        }}
                    />
                </div>
            </div>
        </section>
    );
}

export default Auctionsdetails;
